import yoda from '@kibt/bridge';
import { useEffect, useState } from 'react';
import { Dimensions } from 'react-native';
import { useDebounceFn } from 'ahooks';
import { radar } from '@/common/log';
import { appVersion, screenHeight } from '@/common/device-info';
import { isIOS } from '@kibt/krn-utils';
import { versionCompare } from '@kibt/web-utils/version';
import { getQuery } from '@/common/const';

// 记录容器的高度，useDraggableHeight() 被多次调用时，第二次之后的调用初始化状态可以保证正确
// 记录容器的高度
let fullPageHeight = Dimensions.get('window').height || 500;
// 记录拖动的实时高度
let currPageHeight = Dimensions.get('window').height || 500;

export function useDraggableHeight(props: { distance?: number; initFullScreen?: boolean; debounceTime?: number } = {}) {
    const { initFullScreen = true, debounceTime = 300 } = props;

    const [state, setState] = useState({
        // 新：是否是全屏
        isFullScreen: initFullScreen,
        // 新：兼容useDraggableHeight写法，把几个hooks合并合并
        pageStatus: 'fullScreen' as 'halfScreen' | 'fullScreen',
        // 容器全屏时的高度，首次容器打开半屏时，如果没有推到全屏则获取不准，必须推到一次全屏状态才准确
        fullPageHeight,
        // 新：容器高度
        pageHeight: currPageHeight,
        // 旧：status（1表示半屏，0表示全屏）
        state: 0,
        // 旧：容器高度
        height: currPageHeight,
        init: false,
    });

    const { run: onContainerHeightChange } = useDebounceFn(
        async (draggableRes = undefined) => {
            let _draggableRes;
            // ios和Android draggable_height回调的参数格式不一样
            try {
                if (draggableRes) _draggableRes = typeof draggableRes === 'object' ? draggableRes : JSON.parse(draggableRes);
            } catch (e) {
                // console.log(e)
            }
            let res;
            if (_draggableRes?.height) {
                // 注意IOS和安卓的callback入参不一样 {"height": 725, "isHalf": "1"} isHalf:1表示半屏
                // draggable_height callback入参 {height: xxx, status: 1, url:xxx} status:1 表示半屏
                res = {
                    height: _draggableRes.height,
                    isHalf: Number(_draggableRes.status ?? _draggableRes.isHalf),
                };
                console.log('[usePageHeight] 0', res);
            } else {
                const isHalf = getQuery('isHalf');
                if (isIOS && versionCompare(appVersion, '10.4.20') === -1 && isHalf !== '1') {
                    res = {
                        isFullScreen: true,
                        pageStatus: 'fullScreen',
                        fullPageHeight: currPageHeight,
                        pageHeight: currPageHeight,
                        state: 0,
                        height: currPageHeight,
                        init: false,
                    };
                } else {
                    // 初始化从容器参数获取半屏的高度比例
                    res = await yoda.component.isHalfContainerStatus({}); // {"height": 896, "isHalf": 0, "result": 1}
                    // @warning: ios在某些机型会返回height: 0
                    console.log('[usePageHeight] 1', res);
                    if (res?.height === 0) {
                        radar.event({
                            name: 'HalfContainerStatusError',
                            params: {
                                res,
                                screenHeight: screenHeight,
                                p75: screenHeight * 0.75,
                            },
                        });
                    }
                    if (res.result !== 1) {
                        res = {
                            isFullScreen: initFullScreen,
                            pageStatus: 'fullScreen',
                            fullPageHeight: currPageHeight,
                            pageHeight: currPageHeight,
                            state: 0,
                            height: currPageHeight,
                            init: false,
                        };
                    }
                }
            }
            const { height: _height, isHalf = false } = res || ({} as any);
            const height = Number(_height || 400);
            // 记录实时高度
            currPageHeight = height;
            // 记录全屏高度
            if (!isHalf) {
                fullPageHeight = height;
            }
            if (height !== state.pageHeight || Boolean(!res.isHalf) !== Boolean(state.isFullScreen)) {
                setState(pre => ({
                    ...pre,
                    isFullScreen: !isHalf,
                    pageStatus: isHalf ? 'halfScreen' : 'fullScreen',
                    pageHeight: height,
                    state: isHalf ? 1 : 0,
                    // ios在某些机型会返回height: 0,兜底400高度
                    height: Math.max(height, 400),
                    init: true,
                    // 设置容器全屏的高度
                    fullPageHeight: isHalf ? pre.fullPageHeight : height,
                }));
                console.log(
                    '[usePageHeight] 容器高度改变，触发setState: ',
                    `height: ${height}, pageHeight: ${state.pageHeight}, equals: ${_height === state.pageHeight}`,
                    `isHalf: ${Boolean(isHalf)}, isFullScreen: ${Boolean(state.isFullScreen)}, equals: ${
                        Boolean(!isHalf) === Boolean(state.isFullScreen)
                    }`
                );
            } else {
                if (!state.init)
                    setState(pre => ({
                        ...pre,
                        init: true,
                        // 设置容器全屏的高度
                        fullPageHeight: isHalf ? pre.fullPageHeight : height,
                    }));
                console.log(
                    '[usePageHeight] 容器高度未改变，不触发setState',
                    `height: ${height}, pageHeight: ${state.pageHeight}, equals: ${_height === state.pageHeight}`,
                    `isHalf: ${Boolean(isHalf)}, isFullScreen: ${Boolean(state.isFullScreen)}, equals: ${
                        Boolean(!isHalf) === Boolean(state.isFullScreen)
                    }`
                );
            }
        },
        {
            wait: debounceTime,
        }
    );

    useEffect(() => {
        // 第一次主动请求
        onContainerHeightChange();

        // 后面增加高度监听
        yoda.event.addEventListener('draggable_height' as any, onContainerHeightChange);
        return () => {
            yoda.event.removeEventListener('draggable_height' as any, onContainerHeightChange);
        };
    }, []);

    return state;
}

/**
 * @deprecated 使用 useDraggableHeight 代替
 */
export function useContainerStatus() {
    const [data, setData] = useState<{ status: 'halfScreen' | 'fullScreen'; height: number }>({
        status: null,
        height: 0,
    });
    const refreshContainerStatus = () => {
        return yoda.component.isHalfContainerStatus({}).then(res => {
            const _res = {
                status: res.isHalf ? 'halfScreen' : 'fullScreen',
                height: res.height,
            };
            if (JSON.stringify(_res) !== JSON.stringify(data)) {
                setData({
                    status: res.isHalf ? 'halfScreen' : 'fullScreen',
                    height: res.height,
                });
                console.log('refreshContainerStatus 1', _res);
                return _res;
            }
            console.log('refreshContainerStatus 2', data);
            return data;
        });
    };
    useEffect(() => {
        refreshContainerStatus();
    }, []);
    return {
        ...data,
        refreshContainerStatus,
    };
}
